import { createContext, useMemo } from 'react'
import { cn } from './internal/utils'

/**
 * antd's Sider tells a nested inline Menu to collapse through context, which is
 * why the sidemenu never passes `inlineCollapsed` itself. Menu reads this.
 */
export const SiderContext = createContext(null)

/**
 * antd Layout. index.css paints `.ui-layout` and `.ui-layout-footer`, so those
 * hooks stay. The admin chrome itself is rebuilt in Phase 5; this exists for the
 * handful of pages that use Layout directly.
 */
const Layout = ({ hasSider = false, className, style, children, ...rest }) => (
  <section
    className={cn('ui-layout flex min-h-0 flex-1', hasSider ? 'flex-row' : 'flex-col', className)}
    style={style}
    {...rest}
  >
    {children}
  </section>
)

const Header = ({ className, style, children, ...rest }) => (
  <header
    className={cn('ui-layout-header flex shrink-0 items-center px-4', className)}
    style={style}
    {...rest}
  >
    {children}
  </header>
)

const Content = ({ className, style, children, ...rest }) => (
  <main className={cn('ui-layout-content min-h-0 flex-1', className)} style={style} {...rest}>
    {children}
  </main>
)

const Footer = ({ className, style, children, ...rest }) => (
  <footer
    className={cn('ui-layout-footer shrink-0 px-4 py-3 text-center text-sm', className)}
    style={style}
    {...rest}
  >
    {children}
  </footer>
)

const Sider = ({
  collapsed = false,
  collapsedWidth = 80,
  width = 200,
  trigger,
  collapsible = false,
  onCollapse,
  breakpoint,
  theme: siderTheme,
  className,
  style,
  children,
  ...rest
}) => {
  const siderContext = useMemo(() => ({ collapsed, collapsedWidth }), [collapsed, collapsedWidth])

  return (
    <SiderContext.Provider value={siderContext}>
      <aside
        className={cn(
          'ui-layout-sider relative shrink-0 transition-[width] duration-[var(--duration-normal)] ease-[var(--easing-standard)]',
          collapsed && 'ui-layout-sider-collapsed',
          className
        )}
        style={{ width: collapsed ? collapsedWidth : width, ...style }}
        {...rest}
      >
        {children}
        {collapsible && trigger !== null ? (
          <button
            type="button"
            aria-label={collapsed ? 'Déplier le menu' : 'Replier le menu'}
            aria-expanded={!collapsed}
            onClick={() => onCollapse?.(!collapsed)}
            className="ui-layout-sider-trigger absolute bottom-0 left-0 flex h-11 w-full items-center justify-center border-t border-[var(--border-color)]"
          >
            {trigger}
          </button>
        ) : null}
      </aside>
    </SiderContext.Provider>
  )
}

Layout.Header = Header
Layout.Content = Content
Layout.Footer = Footer
Layout.Sider = Sider

export { Content, Footer, Header, Sider }
export default Layout
